import { FC } from 'react'
import styles from './GoldStandardTrackingSlider.module.scss'
import { GoldStandardCardProps, GoldStandardTrackingSliderProps } from './GoldStandardTrackingSlider.types'
import { Img } from 'components/basic/Img'
import { Button } from 'components/Phantom/Button'
import { generateID } from 'components/_utils/idUtils'
import { useSlider } from 'components/_hooks/useSlider'
import { Icon } from 'components/Phantom/Icon'
import { Type } from 'components/Type'

export const GoldStandardTrackingSlider: FC<GoldStandardTrackingSliderProps> = (props) => {
	const { cards = defaultCards } = props

	const [sliderRef, currentSlide, goToSlide] = useSlider()

	const onPrevClick = () => {
		goToSlide(Math.max(0, currentSlide - 1))
	}
	const onNextClick = () => {
		goToSlide(Math.min(cards.length - 1, currentSlide + 1))
	}

	return (
		<section className={styles.container}>
			<Type.Headline2 animateOnScroll>Gold-standard sleep and health tracking accuracy</Type.Headline2>
			<ul
				ref={sliderRef}
				className={styles.slider}
			>
				{cards.map((card) => (
					<li key={card.tag}>
						<Card {...card} />
					</li>
				))}
			</ul>
			<ul className={styles.mobile_list}>
				{cards.map((card) => (
					<li key={card.tag}>
						<Card {...card} />
					</li>
				))}
			</ul>

			<div className={styles.buttons}>
				<Button.Empty
					id={`gold-standard-slider-prev`}
					onClick={onPrevClick}
					style={{
						opacity: currentSlide === 0 ? 0.5 : 1,
					}}
				>
					<Icon name={'ChevronLeftDark'} />
				</Button.Empty>
				<Button.Empty
					id={`gold-standard-slider-next`}
					onClick={onNextClick}
					style={{
						opacity: currentSlide === cards.length - 1 ? 0.5 : 1,
					}}
				>
					<Icon name={'ChevronRightDark'} />
				</Button.Empty>
			</div>
		</section>
	)
}

const defaultCards: GoldStandardCardProps[] = [
	{
		img: {
			src: 'https://eight-eightsleep-react.s3.us-east-2.amazonaws.com/assets/gold_standard_heart_rate.jpg',
			alt: 'A man lying on the Pod 4 Ultra looking at his phone.',
		},
		tag: 'Heart rate accuracy',
		title: 'HR is 99% accurate compared to the gold standard (ECG)',
		cta: {
			href: '/blog/hrv-accuracy/',
			text: 'Read more',
		},
	},
	{
		img: {
			src: 'https://eight-eightsleep-react.s3.us-east-2.amazonaws.com/assets/gold_standard_respiratory.jpg',
			alt: 'A screenshot of the Eight Sleep app against a purple background. The phone shows a "Breath rate" graph, with the following data: Breath rate today was 14.5/min (in range). Your typical range is 13.6-16.6/min',
		},
		tag: 'Respiratory rate accuracy',
		title: 'RR is 98% accurate compared to gold standard (respiratory inductance plethysmography)',
		cta: {
			href: '/blog/respiratory-rate-accuracy/',
			text: 'Read more',
		},
	},
	{
		img: {
			src: 'https://eight-eightsleep-react.s3.us-east-2.amazonaws.com/assets/gold_standard_sleep_stage.jpg',
			alt: 'A woman sleeping on the Pod 4 Ultra.',
		},
		tag: 'Sleep stage accuracy',
		title: 'New deep learning sleep staging algorithm',
		cta: {
			href: '/blog/eight-sleeps-new-deep-learning-sleep-algorithm/',
			text: 'Read more',
		},
	},
]

const Card: FC<GoldStandardCardProps> = (props) => {
	const { img, tag, title, cta } = props

	return (
		<div className={styles.card}>
			<Img
				src={img.src}
				alt={img.alt}
				objectFit="cover"
			/>
			<div className={styles.card_content}>
				<Type.Headline4 as={'h3'}>{title}</Type.Headline4>
				<Button.TextLink
					id={`gold-standard-${generateID(tag)}`}
					href={cta.href}
				>
					{cta.text}
				</Button.TextLink>
			</div>
		</div>
	)
}
